/**
 * 뉴스레터 구독 신청을 스티비 주소록에 바로 등록한다.
 *
 * 스티비의 [구독 폼 → 코드로 설치하기]가 만들어 주는 공개 엔드포인트를 그대로 쓴다.
 * API 키가 필요 없고(= 무료 요금제에서도 동작하고) 서버도 필요 없다.
 * 주소록 API(api.stibee.com, AccessToken 헤더)는 스탠다드 요금제 이상에서만 열리므로
 * 여기서는 쓰지 않는다.
 *
 * 이 엔드포인트는 원래 브라우저가 폼을 통째로 제출하고 스티비 완료 페이지로
 * 이동하는 용도지만, 응답에 `Access-Control-Allow-Origin: *` 가 붙어 있어
 * fetch 로 호출하고 결과까지 읽을 수 있다. 덕분에 방문자를 우리 페이지에
 * 그대로 둔 채 등록할 수 있다.
 *
 * 응답은 JSON이 아니라 HTML이고, 실패해도 상태 코드는 200이다.
 * 실패는 본문 맨 앞의 `<!--@ERROR_MSG:...-->` 주석으로만 구분된다.
 *
 * 설정값은 .env 의 REACT_APP_STIBEE_* 에서 읽는다.
 * listId 는 어차피 구독 폼 HTML에 그대로 드러나는 공개 값이라 저장소에 두어도 된다.
 */

// 스티비 콘솔의 [주소록 → 구독 관리 화면 → 구독 폼 → 코드로 설치하기]에서
// form action 의 lists/ 와 /public 사이에 있는 값. 주소창의 숫자 ID가 아니라
// `fRnLNUsuk-zcIyLAI4rwmNU=` 같은 문자열이다.
const LIST_ID = (process.env.REACT_APP_STIBEE_LIST_ID || "").trim();

// 선택. 구독자를 특정 그룹으로 분류하고 싶을 때만 채운다. 쉼표로 여러 개.
const GROUP_IDS = (process.env.REACT_APP_STIBEE_GROUP_IDS || "").trim();

export const isStibeeConfigured = Boolean(LIST_ID);

// 실패 응답 본문 맨 앞에 붙는 주석. 예: <!--@ERROR_MSG:잘못된 접근 입니다.-->
const ERROR_MARKER = /<!--\s*@ERROR_MSG:([\s\S]*?)-->/;

function stibeeError(code, message) {
  const error = new Error(message || code);
  error.code = code;
  return error;
}

/**
 * 구독자 1명을 스티비 주소록에 추가한다.
 *
 * 스티비는 이메일이 필수라 이메일 없이는 호출할 수 없다.
 * 주소록에서 [구독 확인 이메일]을 켜 두었다면 여기서 성공해도 아직 '구독 대기' 상태이고,
 * 방문자가 확인 메일의 링크를 눌러야 최종 등록된다.
 *
 * 이미 구독 중인 주소를 다시 넣는 것도 실패로 돌아온다 — 호출하는 쪽에서
 * 방문자에게 보여줄 오류인지 판단해야 한다.
 */
export async function addStibeeSubscriber({ email, name }) {
  if (!isStibeeConfigured) {
    throw stibeeError("STIBEE_NOT_CONFIGURED");
  }

  if (!email) {
    throw stibeeError("STIBEE_EMAIL_REQUIRED");
  }

  const body = new URLSearchParams();
  body.set("email", email);
  if (name) {
    body.set("name", name);
  }
  if (GROUP_IDS) {
    body.set("groupIds", GROUP_IDS);
  }

  // LIST_ID 에는 `=` 가 들어갈 수 있는데, 스티비가 내보내는 폼도 인코딩 없이
  // 경로에 그대로 넣으므로 여기서도 그대로 붙인다.
  const response = await fetch(
    `https://stibee.com/api/v1.0/lists/${LIST_ID}/public/subscribers`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
      },
      body: body.toString(),
    }
  );

  if (!response.ok) {
    throw stibeeError("STIBEE_HTTP_ERROR", `HTTP ${response.status}`);
  }

  const html = await response.text();
  const failure = html.match(ERROR_MARKER);

  if (failure) {
    throw stibeeError("STIBEE_REJECTED", failure[1].trim());
  }
}
